import React, { Suspense, lazy, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { setTenantTimeZone, setTenantDateFormat } from './shared/tenantClock';
import { setTenantNumberFormat, setTenantCurrencyLabel, setTenantHomeCurrency } from './shared/numberFormat';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { HelpCircle, Search } from 'lucide-react';
import './App.css';

import Sidebar from './shared/Sidebar';
import UserMenu from './shared/UserMenu';
import NotificationBell from './shared/NotificationBell';
import Dashboard from './modules/dashboard/Dashboard';
import SalesDashboard from './modules/sales/SalesDashboard';
import CrmDashboard from './modules/crm/CrmDashboard';
import CurrencyTable from './modules/foundation/Currencies/CurrencyTable';
import AccountCategoryTable from './modules/foundation/AccountCategories/AccountCategoryTable';
import AccountClassTable from './modules/foundation/AccountClasses/AccountClassTable';
import AccountTypeTable from './modules/foundation/AccountTypes/AccountTypeTable';
import AccountTable from './modules/foundation/Accounts/AccountTable';
import ProductTable from './modules/product/Products/ProductTable';
import ProductCategoryTable from './modules/inventory/ProductCategories/ProductCategoryTable';
import BrandTable from './modules/inventory/Brands/BrandTable';
import BrandTypeTable from './modules/inventory/BrandTypes/BrandTypeTable';
import SupplierTable from './modules/purchasing/Suppliers/SupplierTable';
import SupplierRebateRuleTable from './modules/purchasing/SupplierRebates/SupplierRebateRuleTable';
import SupplierRebateSkuRateTable from './modules/purchasing/SupplierRebates/SupplierRebateSkuRateTable';
import UomTable from './modules/inventory/Uoms/UomTable';
import ProductTypeTable from './modules/inventory/ProductTypes/ProductTypeTable';
import UomControlTypeTable from './modules/inventory/UomControlTypes/UomControlTypeTable';
import UomControlTypeUsagePage from './modules/inventory/UomControlTypes/UomControlTypeUsagePage';
import OperationTypeTable from './modules/warehouse/OperationTypes/OperationTypeTable';
import PaymentTermTable from './modules/sales/PaymentTerms/PaymentTermTable';
import ShippingServiceTable from './modules/sales/ShippingServices/ShippingServiceTable';
import AustraliaPostPage from './modules/sales/AustraliaPost/AustraliaPostPage';
import ShippingOptionTable from './modules/sales/ShippingOptions/ShippingOptionTable';
import CustomerTierTable from './modules/sales/CustomerTiers/CustomerTierTable';
import CommercialEntityTable from './modules/crm/Leads/CommercialEntityTable';
import CommercialEntityDetailPage from './modules/crm/Leads/CommercialEntityDetailPage';
import SourceTable from './modules/crm/Sources/SourceTable';
import EnquiryTopicTable from './modules/crm/EnquiryTopics/EnquiryTopicTable';
import WebsiteEnquiriesPage from './modules/crm/WebsiteEnquiries/WebsiteEnquiriesPage';
import CloseReasonTable from './modules/crm/CloseReasons/CloseReasonTable';
import MarketTable from './modules/crm/Markets/MarketTable';
import ChannelTable from './modules/crm/Channels/ChannelTable';
import OperatorTable from './modules/crm/Operators/OperatorTable';
import LegalEntityTypeTable from './modules/crm/LegalEntityTypes/LegalEntityTypeTable';
import ActivityOutcomeTable from './modules/crm/ActivityOutcomes/ActivityOutcomeTable';
import AddressTypeTable from './modules/crm/AddressTypes/AddressTypeTable';
import PhoneTypeTable from './modules/crm/PhoneTypes/PhoneTypeTable';
import EmailTypeTable from './modules/crm/EmailTypes/EmailTypeTable';
import LinkRoleTypeTable from './modules/crm/LinkRoleTypes/LinkRoleTypeTable';
import RelationshipTypeTable from './modules/crm/RelationshipTypes/RelationshipTypeTable';
import EntityCardsGrid from './modules/crm/RelationshipTypes/EntityCardsGrid';
import CustomerPipelinePage from './modules/crm/Leads/CustomerPipelinePage';
import ActivitiesPage from './modules/crm/Activities/ActivitiesPage';
import AllEntitiesTable from './modules/market-intel/AllEntities/AllEntitiesTable';
import MarketIntelDashboard from './modules/market-intel/MarketIntelDashboard';
import TradeShowTable from './modules/market-intel/TradeShows/TradeShowTable';
import TradeShowAttendeeRoleTable from './modules/market-intel/TradeShowRoles/TradeShowAttendeeRoleTable';
import UserTable from './modules/customer/Users/UserTable';
import RoleTable from './modules/Global/Roles/RoleTable';
import CustomerPriceGroupTable from './modules/sales/CustomerPriceGroups/CustomerPriceGroupTable';
import ProductPriceGroupTable from './modules/sales/ProductPriceGroups/ProductPriceGroupTable';
import PriceRuleTable from './modules/sales/PriceRules/PriceRuleTable';
import PriceTable from './modules/sales/Prices/PriceTable';
import TaxCodeTable from './modules/foundation/TaxCodes/TaxCodeTable';
import TaxHandlingTypeTable from './modules/foundation/TaxHandlingTypes/TaxHandlingTypeTable';
import AccountingSetupPage from './modules/foundation/AccountingSetup/AccountingSetupPage';
import PaymentOptionsPage from './modules/receivables/PaymentOptions/PaymentOptionsPage';
import PriceLookupPage from './modules/sales/Prices/PriceLookupPage';
import QuotesTable from './modules/sales/Quotes/QuotesTable';
import QuotePage from './modules/sales/Quotes/QuotePage';
import SalesOrdersTable from './modules/sales/SalesOrders/SalesOrdersTable';
import ShipmentsTable from './modules/warehouse/ShipmentsTable';
import SalesOrderPage from './modules/sales/SalesOrders/SalesOrderPage';
import PickOrderPage from './modules/warehouse/PickOrderPage';
import FulfilmentPage from './modules/warehouse/FulfilmentPage';
import PackagingTable from './modules/warehouse/Packaging/PackagingTable';
import LabelSizeTable from './modules/warehouse/LabelSizes/LabelSizeTable';
import PackingStationPage from './modules/warehouse/PackingStation/PackingStationPage';
import WeighMeasurePage from './modules/warehouse/WeighMeasure/WeighMeasurePage';
import WarehouseSettingsPage from './modules/warehouse/WarehouseSetup/WarehouseSettingsPage';
import Pricelist2Page from './modules/sales/Prices/Pricelist2Page';
import PricelistTable from './modules/sales/Prices/PricelistTable';
import InventorySettingsPage from './modules/inventory/InventorySettings/InventorySettingsPage';
import IntegrityReportPage from './modules/inventory/Reports/IntegrityReportPage';
import DocumentMilestonesPage from './modules/Global/Reports/DocumentMilestonesPage';
import AuditLogPage from './modules/Global/AuditLog/AuditLogPage';
import PlatformUpdateCheckPage from './modules/Global/PlatformUpdateCheck/PlatformUpdateCheckPage';
import PurchasingIntegrityReportPage from './modules/purchasing/Reports/PurchasingIntegrityReportPage';
import SalesIntegrityReportPage from './modules/sales/Reports/SalesIntegrityReportPage';
import CompanySettings from './modules/foundation/CompanySettings';
import GlobalDashboard from './modules/Global/GlobalDashboard';
import PurchasingDashboard from './modules/purchasing/PurchasingDashboard';
import InventoryDashboard from './modules/inventory/InventoryDashboard';
import WarehouseDashboard from './modules/warehouse/WarehouseDashboard';
import AccountingDashboard from './modules/accounting/AccountingDashboard';
import PlatformSettingsPage from './modules/Global/PlatformSettings/PlatformSettingsPage';
import MyAccountPage from './modules/Global/MyAccount/MyAccountPage';
import SystemAdminPage from './modules/Global/SystemAdmin/SystemAdminPage';
import LoginSwitcherPage from './modules/Global/LoginSwitcher/LoginSwitcherPage';
import ApiIntegrationsPage from './modules/Global/ApiIntegrations/ApiIntegrationsPage';
import QuickBooksPage from './modules/accounting/QuickBooks/QuickBooksPage';
import ReceivablesDashboard from './modules/receivables/ReceivablesDashboard';
import ReceivablesSettingsPage from './modules/receivables/ReceivablesSetup/ReceivablesSettingsPage';
import InvoicesTable from './modules/receivables/Invoices/InvoicesTable';
import InvoicePage from './modules/receivables/Invoices/InvoicePage';
import ReceiptsTable from './modules/receivables/Receipts/ReceiptsTable';
import ReceiptPage from './modules/receivables/Receipts/ReceiptPage';
import SetupChecklistPage from './modules/Global/SetupChecklist/SetupChecklistPage';
import SalesSettingsPage from './modules/sales/SalesSettings/SalesSettingsPage';
import ManualPage from './modules/manual/ManualPage';
import ScreenSettingsTable from './modules/manual/ScreenSettingsTable';
import FieldHelpTable from './modules/manual/FieldHelpTable';
import EmailTemplateTable from './modules/Global/EmailTemplates/EmailTemplateTable';
import ErrorCatalogPage from './modules/manual/ErrorCatalogPage';
import MediaLibraryPage from './modules/Global/MediaLibrary/MediaLibraryPage';
import HelpSheet from './shared/HelpSheet';
import HelpSheetContext from './shared/HelpSheetContext';
import { FieldHelpRegistryProvider } from './shared/FieldHelpRegistryContext';
import { ErrorToastProvider } from './shared/ErrorToastContext';
import ProductUomPage from './modules/product/Products/ProductUomPage';
import ProductMediaPage from './modules/product/Products/ProductMediaPage';
import ProductWeightsPage from './modules/product/Products/ProductWeightsPage';
import ProductCostPricePage from './modules/product/Products/ProductCostPricePage';
import SupplierCostPricesPage from './modules/purchasing/Suppliers/SupplierCostPricesPage';
import PricePricingPage from './modules/sales/Prices/PricePricingPage';
import DocumentTypeTable from './modules/sales/DocumentTypes/DocumentTypeTable';
import { prefetchDefaultPageSize } from './shared/pagination';
import { MODULE_TABS } from './shared/moduleTabs';
import ModuleRail from './shared/ModuleRail';
import { PermissionsContext, ScreenAccessContext, resolveScreenAccess, combineAccess, FULL, NONE } from './shared/PermissionsContext';

const MODULE_NAVIGATION = {
  home: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        { label: 'New Product', pageKey: 'products?new=1' },
        { label: 'New Expense', pageKey: 'home-new-expense' },
        { label: 'New Transfer', pageKey: 'home-new-transfer' },
      ],
    },
    {
      header: 'Help',
      items: [
        { label: 'Manual', pageKey: 'global-manual' },
      ],
    },
  ],
  sales: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'sales-dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        // A Customers list in its own right (CommercialEntityTable's
        // customersOnly mode) — its own saved tabs, separate from CRM's
        // Prospects & Customers.
        { label: 'Customers', pageKey: 'sales-customers' },
        { label: 'Quotes', pageKey: 'sales-quotes' },
        { label: 'Sales Orders', pageKey: 'sales-orders' },
        { label: 'Price Lookup', pageKey: 'sales-price-lookup' },
      ],
    },
    {
      header: 'Lists',
      items: [
        { label: 'Pricelists', pageKey: 'sales-pricelist-2' },
        { label: 'Sales Prices', pageKey: 'sales-prices' },
        { label: 'Price Rules', pageKey: 'sales-price-rules' },
        { label: 'Customer Price Groups', pageKey: 'sales-customer-price-groups' },
        { label: 'Product Price Groups', pageKey: 'sales-product-price-groups' },
        { label: 'Payment Terms', pageKey: 'sales-payment-terms' },
        { label: 'Shipping Services', pageKey: 'sales-shipping-services' },
        { label: 'Shipping Options', pageKey: 'sales-shipping-options' },
      ],
    },
    {
      header: 'Reports',
      items: [
        { label: 'Sales Reports', pageKey: 'sales-reports' },
      ],
    },
    {
      header: 'Setup',
      items: [
        { label: 'Sales Settings', pageKey: 'sales-setup' },
        // Australia Post methods, savings band and Rate Cards (issue #328).
        { label: 'Australia Post', pageKey: 'sales-australia-post' },
      ],
    },
  ],
  crm: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'crm-dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        { label: 'Pipeline', pageKey: 'crm-customer-pipeline' },
        // One list (vis_relationship_lifecycle.md §6) — Default shows every
        // Prospect and Customer, Prospects/Customers are System tabs. The old
        // /customer-customers URL opens it on the Customers tab, see
        // NAV_ALIASES below.
        { label: 'Prospects & Customers', pageKey: 'crm-leads' },
        // Every record's Activities on one page (issue #307).
        { label: 'Activities', pageKey: 'crm-activities' },
        // Website general enquiries waiting for staff (issue #287).
        { label: 'Website Enquiries', pageKey: 'crm-website-enquiries' },
      ],
    },
    {
      header: 'Lists',
      items: [
        { label: 'Customer Tiers', pageKey: 'customer-tiers' },
        { label: 'Close Reasons', pageKey: 'crm-close-reasons' },
        { label: 'Markets', pageKey: 'crm-markets' },
        { label: 'Outlets', pageKey: 'crm-channels' },
        { label: 'Operators', pageKey: 'crm-operators' },
      ],
    },
    {
      header: 'Setup',
      items: [
        { label: 'Sources', pageKey: 'crm-sources' },
        { label: 'Legal Entity Types', pageKey: 'crm-legal-entity-types' },
        { label: 'Activity Outcomes', pageKey: 'crm-activity-outcomes' },
        { label: 'Enquiry Topics', pageKey: 'crm-enquiry-topics' },
      ],
    },
  ],
  purchasing: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'purchasing-dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        { label: 'Purchase Orders', pageKey: 'purchasing-orders' },
        { label: 'Suppliers', pageKey: 'suppliers' },
      ],
    },
    {
      header: 'Supplier Rebates',
      items: [
        { label: 'Rebate SKU Rates', pageKey: 'purchasing-supplier-rebate-sku-rates' },
        { label: 'Rebate Rules', pageKey: 'purchasing-supplier-rebate-rules' },
      ],
    },
    {
      header: 'Lists',
      items: [
        { label: 'Supplier Types', pageKey: 'purchasing-supplier-types' },
        { label: 'Purchase Terms', pageKey: 'purchasing-terms' },
      ],
    },
    {
      header: 'Reports',
      items: [
        { label: 'Purchasing Reports', pageKey: 'purchasing-reports' },
      ],
    },
    {
      header: 'Setup',
      items: [
        { label: 'Purchasing Settings', pageKey: 'purchasing-setup' },
      ],
    },
  ],
  inventory: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'inventory-dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        { label: 'Products', pageKey: 'products' },
        { label: 'Warehouses', pageKey: 'inventory-warehouses' },
      ],
    },
    {
      header: 'Lists',
      items: [
        { label: 'Product Categories', pageKey: 'inventory-product-categories' },
        { label: 'Brands', pageKey: 'inventory-brands' },
        { label: 'Brand Types', pageKey: 'inventory-brand-types' },
        { label: 'Units of Measure', pageKey: 'inventory-uom' },
        { label: 'Product Types', pageKey: 'inventory-product-types' },
        { label: 'UoM Control Types', pageKey: 'inventory-uom-control-types' },
      ],
    },
    {
      header: 'Reports',
      items: [
        { label: 'Product Integrity', pageKey: 'inventory-reports' },
      ],
    },
    {
      header: 'Setup',
      items: [
        { label: 'Inventory Settings', pageKey: 'inventory-setup' },
      ],
    },
  ],
  // Fulfilment for every order type (issue #298) — Pick Order and
  // Shipments moved here from Sales. Issue #306: a page for each stage,
  // each listing the orders waiting at it, plus the Fulfilments overview of
  // every unfinished order (under Lists, with Shipments).
  warehouse: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'warehouse-dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        { label: 'Pick', pageKey: 'warehouse-pick' },
        { label: 'Pack', pageKey: 'warehouse-pack' },
        // Issue #334 — its own permission (OWN_PERMISSION_SCREENS).
        { label: 'Virtual Packer', pageKey: 'warehouse-packing-trial' },
        { label: 'Ship', pageKey: 'warehouse-ship' },
        { label: 'Weigh & Measure', pageKey: 'warehouse-weigh-measure' },
      ],
    },
    {
      header: 'Lists',
      items: [
        { label: 'Fulfilments', pageKey: 'warehouse-fulfilment' },
        { label: 'Shipments', pageKey: 'warehouse-shipments' },
      ],
    },
    {
      header: 'Setup',
      items: [
        // Issue #299.
        { label: 'Packaging', pageKey: 'warehouse-packaging' },
        // Issue #305.
        { label: 'Label Sizes', pageKey: 'warehouse-label-sizes' },
        { label: 'Packing Station', pageKey: 'warehouse-packing-station' },
        { label: 'Operation Types', pageKey: 'warehouse-operation-types' },
        { label: 'Warehouse Settings', pageKey: 'warehouse-setup' },
      ],
    },
  ],
  accounting: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'accounting-dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        { label: 'General Ledger', pageKey: 'accounting-ledger' },
        { label: 'Accounts Payable', pageKey: 'accounting-ap' },
        { label: 'QuickBooks', pageKey: 'accounting-quickbooks' },
      ],
    },
    {
      header: 'Lists',
      items: [
        { label: 'Account Categories', pageKey: 'accounting-account-categories' },
        { label: 'Account Classes', pageKey: 'accounting-account-classes' },
        { label: 'Account Types', pageKey: 'accounting-account-types' },
        { label: 'Accounts', pageKey: 'accounting-coa' },
        { label: 'Cost Centers', pageKey: 'accounting-cost-centers' },
        { label: 'Currency Codes', pageKey: 'accounting-currency' },
        { label: 'Tax Handling Types', pageKey: 'accounting-tax-handling-types' },
        { label: 'Tax Codes', pageKey: 'accounting-tax' },
      ],
    },
    {
      header: 'Reports',
      items: [
        { label: 'Accounting Reports', pageKey: 'accounting-reports' },
      ],
    },
    {
      header: 'Setup',
      items: [
        { label: 'Accounting Settings', pageKey: 'accounting-setup' },
      ],
    },
  ],
  receivables: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'receivables-dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        { label: 'Accounts Receivable', pageKey: 'receivables-ar' },
        { label: 'Invoices', pageKey: 'receivables-invoices' },
        { label: 'Receipts', pageKey: 'receivables-receipts' },
      ],
    },
    {
      header: 'Setup',
      items: [
        { label: 'Receivables Settings', pageKey: 'receivables-setup' },
        { label: 'Payment Options', pageKey: 'receivables-payment-options' },
      ],
    },
  ],
  global: [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'global-dashboard' },
      ],
    },
    {
      header: 'Operations',
      items: [
        { label: 'Audit Log', pageKey: 'global-audit' },
        { label: 'Login Switcher', pageKey: 'global-login-switcher' },
        { label: 'Media Library', pageKey: 'global-media-library' },
        { label: 'Document Milestones', pageKey: 'global-document-milestones' },
        { label: 'Email Templates', pageKey: 'global-email-templates' },
        { label: 'Platform Update Check', pageKey: 'global-platform-update-check' },
      ],
    },
    {
      header: 'Lists',
      items: [
        { label: 'Users', pageKey: 'global-users' },
        { label: 'Roles', pageKey: 'global-roles' },
        { label: 'Address Types', pageKey: 'global-address-types' },
        { label: 'Phone Types', pageKey: 'global-phone-types' },
        { label: 'Email Types', pageKey: 'global-email-types' },
        // Cross-module by design — Purchasing (PO), Sales (QO/SO), Manufacturing
        // (WO), and Accounting (Invoice/Credit Note) all share this same
        // numbering/labeling mechanism, so it belongs alongside the other
        // shared reference lookups here rather than under any one module. See
        // the Document Type design interview, 2026-08-22.
        { label: 'Document Types', pageKey: 'global-document-types' },
      ],
    },
    {
      header: 'Reports',
      items: [
        { label: 'Platform Reports', pageKey: 'global-reports' },
      ],
    },
    {
      header: 'Setup',
      // System Admin is local-dev-machine-only tooling (it can destroy the
      // local dev database — see SystemAdminPage.js) — left out of the menu
      // entirely in production, same "never reachable by an actual tenant"
      // treatment as the Manual's own Dev Tools article group.
      items: [
        { label: 'Setup Checklist', pageKey: 'global-setup-checklist' },
        { label: 'Company Information', pageKey: 'company-info' },
        { label: 'Platform Settings', pageKey: 'global-platform-settings' },
        process.env.NODE_ENV !== 'production' && { label: 'System Admin', pageKey: 'global-system-admin' },
        { label: 'API Integrations', pageKey: 'global-api-integrations' },
        { label: 'Screen Help Settings', pageKey: 'global-manual-screens' },
        { label: 'Field Help Settings', pageKey: 'global-field-help' },
        { label: 'Error Catalog', pageKey: 'global-error-catalog' },
      ].filter(Boolean),
    },
  ],
  // Still mostly a placeholder module (competitor tracking, brand
  // intelligence, etc. to grow into) — All Entities is its first real
  // screen: every Commercial Entity regardless of Relationship Type,
  // unlike CRM's own Prospects & Customers screen, which is scoped to the
  // Prospect and Customer Relationship Types.
  'market-intel': [
    {
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', pageKey: 'market-intel-dashboard' },
      ],
    },
    {
      header: 'Lists',
      items: [
        { label: 'All Entities', pageKey: 'market-intel-all-entities' },
        { label: 'Trade Shows', pageKey: 'market-intel-trade-shows' },
      ],
    },
    {
      // Entity-wide setup, used by CRM and Purchasing too. Page keys keep
      // their global- prefix so existing links and Screen Help still match.
      header: 'Setup',
      items: [
        { label: 'Relationship Types', pageKey: 'global-relationship-types' },
        { label: 'Entity Cards', pageKey: 'global-entity-cards' },
        { label: 'Link Role Types', pageKey: 'global-link-role-types' },
        { label: 'Trade Show Roles', pageKey: 'market-intel-trade-show-roles' },
      ],
    },
  ],
};

const MODULE_PAGE_FALLBACK = {
  home: 'dashboard',
  sales: 'sales-dashboard',
  purchasing: 'purchasing-dashboard',
  inventory: 'inventory-dashboard',
  warehouse: 'warehouse-dashboard',
  accounting: 'accounting-dashboard',
  receivables: 'receivables-dashboard',
  global: 'global-dashboard',
  crm: 'crm-dashboard',
  'market-intel': 'market-intel-dashboard',
};

// A module Dashboard with no cards of its own yet shows only its title —
// never sample figures, which staff could mistake for their own data.
function PlaceholderPage({ title, moduleLabel, isDashboard }) {
  if (!isDashboard) {
    return (
      <div className="placeholder-page">
        <h2>{title}</h2>
        <p>{moduleLabel} module page is ready for implementation.</p>
      </div>
    );
  }

  return (
    <div className="dashboard dashboard-page">
      <div className="dashboard-content">
        <div className="dashboard-content-inner">
          <div className="dashboard-page-header">
            <div className="dashboard-page-header-main">
              <h2 className="dashboard-page-title">{title}</h2>
              <p className="dashboard-page-description">
                The {moduleLabel} Dashboard has no cards yet.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Every pageKey in MODULE_NAVIGATION maps 1:1 to a URL path (`/${pageKey}`,
// with 'dashboard' as the index route '/'). Flattened once here so route
// generation and module look-up don't re-walk the nested structure.
const ALL_NAV_ITEMS = Object.entries(MODULE_NAVIGATION).flatMap(([moduleKey, sections]) =>
  sections.flatMap((section) => section.items.map((item) => ({ ...item, moduleKey })))
);

// Detail routes reached by clicking into a list row (e.g. a Price's own
// Pricing screen), not via direct sidebar nav — so they're outside
// ALL_NAV_ITEMS and need registering by hand for Screen Help to know about
// them at all. products-uom needs a dedicated pageKey (not just its
// route's first segment) since that segment is 'products', which would
// otherwise collide with the Products list page itself — see helpPageKey
// below. Pricing (every Group, Retail included) no longer has a
// Product-nested route at all — it lives entirely under Sales > Prices.
const EXTRA_SCREENS = [
  { pageKey: 'prices', label: 'Price — Pricing', moduleKey: 'sales' },
  { pageKey: 'products-uom', label: 'Product — Units of Measure', moduleKey: 'inventory' },
  { pageKey: 'inventory-uom-control-types-usage', label: 'UoM Control Type — In Use', moduleKey: 'inventory' },
  { pageKey: 'customer-customers', label: 'Prospects & Customers — Customers', moduleKey: 'crm' },
];

// A screen reached at its own URL but shown in the sidebar as another
// page's item — /customer-customers opens Prospects & Customers on its
// Customers tab (drill-throughs, breadcrumbs, its own Screen Help row)
// while the sidebar highlights the one Prospects & Customers item.
const NAV_ALIASES = {
  'customer-customers': 'crm-leads',
};

// Screens' URLs from before they moved module (Pick Order and Shipments to
// Warehouse, issue #298; Invoices, Receipts, Accounts Receivable and Payment
// Options to Receivables, issue #444), kept working for bookmarks: each
// redirects to its new screen.
const MOVED_SCREENS = {
  'sales-pick': 'warehouse-pick',
  'sales-shipments': 'warehouse-shipments',
  'accounting-ar': 'receivables-ar',
  'accounting-invoices': 'receivables-invoices',
  'accounting-receipts': 'receivables-receipts',
  'accounting-payment-options': 'receivables-payment-options',
};

function MovedScreenRedirect({ to }) {
  const location = useLocation();
  const { id } = useParams();
  return <Navigate to={`/${to}${id ? `/${id}` : ''}${location.search}`} state={location.state} replace />;
}

// Permissions (issue #252). Each menu screen's module, for resolving a
// Role's module-level permissions onto it — deep links like
// 'products?new=1' are left out so they don't claim the screen for Home.
const SCREEN_MODULE = Object.fromEntries(
  ALL_NAV_ITEMS.filter((item) => !item.pageKey.includes('?')).map((item) => [item.pageKey, item.moduleKey])
);

// Only staff holding Admin can open these, whatever their Roles say, so
// they're not in the Role editor's grid either.
const ADMIN_ONLY_SCREENS = new Set(['global-users', 'global-roles', 'global-system-admin', 'global-login-switcher']);

// Screens with their own permission: a Role reaches one only when it's
// ticked against the screen itself, never through its module. Same list as
// the backend's permissionCatalog.js OWN_PERMISSION_SCREENS.
const OWN_PERMISSION_SCREENS = new Set(['warehouse-packing-trial']);

// The Virtual Packer's 3D view brings three.js, so it loads only when opened.
const PackingTrialPage = lazy(() => import('./modules/warehouse/PackingTrial/PackingTrialPage'));

// Routes that aren't a menu screen's own page key, mapped to the screen(s)
// whose permissions govern them. A record reachable from several screens
// takes the best of them (same as backend config/permissionCatalog.js).
const ROUTE_SCREENS = {
  quotes: ['sales-quotes'],
  pricelists: ['sales-pricelist-2'],
  'commercial-entities': ['crm-leads', 'crm-customer-pipeline', 'sales-customers', 'market-intel-all-entities'],
  'customer-customers': ['crm-leads'],
  ...Object.fromEntries(Object.entries(MOVED_SCREENS).map(([from, to]) => [from, [to]])),
};

// The screen whose permissions decide whether a menu item shows.
function itemPermissionKey(item) {
  return item.pageKey.split('?')[0];
}

function NoAccessPage() {
  return (
    <div className="placeholder-page">
      <h2>No access</h2>
      <p>Your roles don't include this screen. Ask an Admin if you need it.</p>
    </div>
  );
}

function navPageKeyFor(pageKey) {
  return NAV_ALIASES[pageKey] || pageKey;
}

function findModuleForPageKey(pageKey) {
  const navKey = navPageKeyFor(pageKey);
  const item = ALL_NAV_ITEMS.find((navItem) => navItem.pageKey === navKey);
  return item ? item.moduleKey : null;
}

function getModuleLabel(moduleKey) {
  return (MODULE_TABS.find((module) => module.key === moduleKey) || MODULE_TABS[0]).label;
}

// Global search over the module nav tree: modules (matched by their tab
// label) plus every page in MODULE_NAVIGATION (matched by page label).
// Label-starts-with results rank above mid-label matches; each module can
// only appear once, ahead of its own pages, so typing a module name (e.g.
// "CRM") surfaces the module jump before any of its individual pages.
function searchNavItems(query, navItems, moduleFallback) {
  const term = query.trim().toLowerCase();
  if (!term) return [];

  const visibleModules = new Set(navItems.map((item) => item.moduleKey));
  const moduleMatches = MODULE_TABS
    .filter((module) => visibleModules.has(module.key) && module.label.toLowerCase().includes(term))
    .map((module) => ({
      key: `module-${module.key}`,
      type: 'module',
      label: module.label,
      moduleKey: module.key,
      pageKey: moduleFallback(module.key),
      startsWith: module.label.toLowerCase().startsWith(term),
    }));

  const pageMatches = navItems
    .filter((item) => item.label.toLowerCase().includes(term))
    .map((item) => ({
      key: `page-${item.moduleKey}-${item.pageKey}`,
      type: 'page',
      label: item.label,
      moduleKey: item.moduleKey,
      pageKey: item.pageKey,
      startsWith: item.label.toLowerCase().startsWith(term),
    }));

  return [...moduleMatches, ...pageMatches]
    .sort((a, b) => (a.startsWith === b.startsWith ? 0 : a.startsWith ? -1 : 1))
    .slice(0, 8);
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPageKey = location.pathname === '/' ? 'dashboard' : location.pathname.slice(1).split('/')[0];
  const derivedModule = useMemo(() => findModuleForPageKey(currentPageKey), [currentPageKey]);

  // Screen Help needs a more specific key than currentPageKey for the two
  // Products kebab-detail routes, since both nest under '/products' and
  // would otherwise share the Products list page's own Screen Settings row
  // (same code, same enabled flag, same article). Sidebar highlighting and
  // module derivation above intentionally keep using the plain
  // currentPageKey — only the help lookup needs this finer-grained key.
  const helpPageKey = useMemo(() => {
    const segments = location.pathname.slice(1).split('/');
    if (segments[0] === 'products' && segments[2] === 'uom') return 'products-uom';
    if (segments[0] === 'inventory-uom-control-types' && segments[2] === 'uoms') return 'inventory-uom-control-types-usage';
    return currentPageKey;
  }, [location.pathname, currentPageKey]);

  const [activeModule, setActiveModule] = useState(derivedModule || 'home');
  // Whether the full section sidebar is shown at all, vs. fully hidden so
  // only the module rail's icon column is visible. Its toggle lives at the
  // top of the rail, above the module icons — lifted here since both it
  // and the sidebar need to agree on this state. Defaults to collapsed:
  // the rail's hover flyouts already cover quick navigation.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebar_icon_only') !== 'false');
  const [companyInfo, setCompanyInfo] = useState({ company_name: '', abn: '' });
  // Nothing renders until the tenant Timezone, Date Format, Number Format
  // and Currency Label are known, so no screen ever derives "today" from
  // the Australia/Sydney fallback or shows a date, number or amount in the
  // wrong format (shared/tenantClock.js, shared/numberFormat.js).
  const [companyInfoLoaded, setCompanyInfoLoaded] = useState(false);
  // The signed-in staff member's Roles (issue #252, /api/auth/me), read on
  // every page load so a Role change applies without signing out. Nothing
  // renders until it's known, so no screen flashes up before its menu item
  // and actions are hidden.
  const [access, setAccess] = useState(null);
  // Retried while the server can't be answered (a restart, a cold start),
  // so one dropped request doesn't leave every screen on "No access" until
  // a refresh. A 401 isn't retried: axiosAuth.js signs the browser out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let attempt = 1; ; attempt++) {
        try {
          const res = await axios.get('/api/auth/me');
          if (!cancelled) setAccess(res.data.data?.access || { isAdmin: true, roles: [] });
          return;
        } catch (err) {
          if (cancelled) return;
          if (err.response?.status === 401 || attempt >= 10) {
            setAccess({ isAdmin: false, roles: [] });
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const isAdmin = !!access?.isAdmin;
  const accessFor = useCallback((pageKey) => {
    if (ADMIN_ONLY_SCREENS.has(pageKey)) return isAdmin ? FULL : NONE;
    // Not a permissioned screen (e.g. the Not Found page).
    if (!SCREEN_MODULE[pageKey]) return FULL;
    return resolveScreenAccess(access, pageKey, OWN_PERMISSION_SCREENS.has(pageKey) ? null : SCREEN_MODULE[pageKey]);
  }, [access, isAdmin]);
  const permissionsValue = useMemo(() => ({ isAdmin, accessFor }), [isAdmin, accessFor]);

  // The menu as this staff member sees it: screens without Read (or, for a
  // "New ..." deep link, without Create) are left out, then any section or
  // module with nothing left.
  const visibleNavigation = useMemo(() => {
    const result = {};
    for (const [moduleKey, sections] of Object.entries(MODULE_NAVIGATION)) {
      const visibleSections = sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => accessFor(itemPermissionKey(item))[item.pageKey.includes('new=1') ? 'create' : 'read']),
        }))
        .filter((section) => section.items.length);
      if (visibleSections.length) result[moduleKey] = visibleSections;
    }
    return result;
  }, [accessFor]);
  // Counts beside menu items (issue #276): Sales Orders shows how many
  // portal orders are awaiting acceptance, and Website Enquiries how many
  // enquiries wait for staff. Each loads only for staff who can read its
  // screen. Refreshed on each page change and every minute; no count is
  // shown at zero.
  const canSeeSalesOrders = !!access && accessFor('sales-orders').read;
  const canSeeWebsiteEnquiries = !!access && accessFor('crm-website-enquiries').read;
  const [navCounts, setNavCounts] = useState({});
  useEffect(() => {
    const sources = [
      canSeeSalesOrders && ['sales-orders', '/api/sales-orders/awaiting-portal-count'],
      canSeeWebsiteEnquiries && ['crm-website-enquiries', '/api/website-enquiries/waiting-count'],
    ].filter(Boolean);
    if (!sources.length) return undefined;
    let cancelled = false;
    const load = () => sources.forEach(([pageKey, url]) => axios.get(url)
      .then((res) => { if (!cancelled) setNavCounts((prev) => ({ ...prev, [pageKey]: res.data.data?.count || 0 })); })
      .catch(() => {}));
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [canSeeSalesOrders, canSeeWebsiteEnquiries, location.pathname]);
  const visibleNavItems = useMemo(() => Object.entries(visibleNavigation).flatMap(([moduleKey, sections]) =>
    sections.flatMap((section) => section.items.map((item) => ({ ...item, moduleKey })))
  ), [visibleNavigation]);
  // A module's landing page: its usual default if that's visible, else its
  // first visible screen.
  const moduleFallback = useCallback((moduleKey) => {
    const items = visibleNavItems.filter((item) => item.moduleKey === moduleKey && !item.pageKey.includes('?'));
    const preferred = items.find((item) => item.pageKey === MODULE_PAGE_FALLBACK[moduleKey]);
    return (preferred || items[0] || { pageKey: 'dashboard' }).pageKey;
  }, [visibleNavItems]);
  // The current route's permissions, for its screen's New / Save / Edit /
  // Delete / Archive actions (useScreenAccess in shared/PermissionsContext.js).
  const currentAccess = useMemo(() => {
    const keys = ROUTE_SCREENS[currentPageKey] || [currentPageKey];
    return combineAccess(keys.map(accessFor));
  }, [currentPageKey, accessFor]);

  const [globalSearch, setGlobalSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchContainerRef = useRef(null);
  const searchResults = useMemo(() => searchNavItems(globalSearch, visibleNavItems, moduleFallback), [globalSearch, visibleNavItems, moduleFallback]);
  // Customer/Lead record matches — a separate, debounced network lookup
  // (searchNavItems above is a pure client-side filter over the static nav
  // tree, so it can't know about individual records) merged into the same
  // dropdown as the module/page matches.
  const [recordResults, setRecordResults] = useState([]);
  useEffect(() => {
    const term = globalSearch.trim();
    if (term.length < 2) {
      setRecordResults([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      axios.get('/api/commercial-entities/quick-search', { params: { q: term }, signal: controller.signal })
        .then((response) => setRecordResults(response.data.data || []))
        .catch(() => {});
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [globalSearch]);
  // Product/SKU matches — same debounced-network-lookup shape as
  // recordResults above, hitting the products API (which already searches
  // sku/name/supplier_sku) instead of commercial-entities. Selecting one
  // lands on the Products list pre-filtered to that SKU, same as typing it
  // into that screen's own search box.
  const [productResults, setProductResults] = useState([]);
  useEffect(() => {
    const term = globalSearch.trim();
    if (term.length < 2) {
      setProductResults([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      axios.get('/api/products', { params: { search: term, sortBy: 'sku', sortOrder: 'ASC', limit: 8 }, signal: controller.signal })
        .then((response) => setProductResults(response.data.data || []))
        .catch(() => {});
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [globalSearch]);
  const combinedSearchResults = useMemo(() => [
    ...searchResults,
    ...recordResults.map((entity) => ({
      key: `record-${entity.id}`,
      type: 'record',
      label: entity.name,
      entityId: entity.id,
      isCustomer: entity.isCustomer,
      stage: entity.stage,
    })),
    // Each SKU match offers both targets it might be wanted for — the
    // Product record itself, and any Prices scoped to it
    // (product_search there is the same substring match as here, so the
    // exact SKU picked still only lands on rows actually touching it).
    ...productResults.flatMap((product) => [
      { key: `product-${product.id}`, type: 'product', label: product.sku, term: product.sku },
      { key: `price-${product.id}`, type: 'price', label: product.sku, term: product.sku },
    ]),
  ], [searchResults, recordResults, productResults]);
  const [manualScreens, setManualScreens] = useState([]);
  const [helpSheetOpen, setHelpSheetOpen] = useState(false);
  // Set by FieldHelp (via HelpSheetContext) to jump the sheet straight to
  // one article, overriding the current screen's own Screen Help article.
  // Cleared on close so the screen's "?" goes back to its own default next
  // time, instead of staying pinned to whatever field link opened it last.
  const [helpSheetArticleOverride, setHelpSheetArticleOverride] = useState(null);
  const openHelpArticle = useCallback((slug) => {
    setHelpSheetArticleOverride(slug);
    setHelpSheetOpen(true);
  }, []);
  const closeHelpSheet = useCallback((open) => {
    setHelpSheetOpen(open);
    if (!open) setHelpSheetArticleOverride(null);
  }, []);
  const loadCompanyInfo = useCallback(async () => {
    try {
      const response = await axios.get('/api/company-settings');
      const settings = response.data?.data || response.data || {};

      setCompanyInfo({
        company_name: settings.company_name || settings.companyName || '',
        abn: settings.abn || '',
      });
      setTenantTimeZone(settings.timezone);
      setTenantDateFormat(settings.date_format);
      setTenantNumberFormat(settings.number_format);
      setTenantCurrencyLabel(settings.currency_label);
      setTenantHomeCurrency(settings.currency_code);
    } catch (error) {
      setCompanyInfo({ company_name: '', abn: '' });
    } finally {
      setCompanyInfoLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadCompanyInfo();
    prefetchDefaultPageSize();
  }, [loadCompanyInfo]);

  // Keeps Screen Settings (manual-screens table) in sync with whatever
  // screens the app actually has, then loads each screen's show/hide flag
  // and reference code so the page-level "?" and the corner code badge
  // below know what to render for the current route. Sync is
  // additive-only server-side — see manualScreenController.syncScreens —
  // so this never resets an admin's existing choices, just adds screens
  // it hasn't seen yet.
  const loadManualScreens = useCallback(async () => {
    try {
      const res = await axios.get('/api/manual-screens', { params: { all: true } });
      setManualScreens(res.data?.data || []);
    } catch (error) {
      setManualScreens([]);
    }
  }, []);

  useEffect(() => {
    const syncManualScreens = async () => {
      const screens = [...ALL_NAV_ITEMS, ...EXTRA_SCREENS].map((item) => ({ page_key: item.pageKey, label: item.label, module_key: item.moduleKey }));
      try {
        await axios.post('/api/manual-screens/sync', { screens });
      } catch (error) {
        // Sync failing just means new screens won't appear in Screen
        // Settings yet — loadManualScreens below still runs so existing
        // flags/codes keep working.
      }
      loadManualScreens();
    };
    syncManualScreens();
    // eslint-disable-next-line
  }, []);

  // Re-reads flags/codes on every navigation (not just once at boot) — so
  // toggling "Show Help" in Screen Settings, then navigating to that screen
  // without a full page reload, actually takes effect.
  useEffect(() => {
    loadManualScreens();
    // eslint-disable-next-line
  }, [helpPageKey]);

  // Keeps the top-tab highlight in sync with the URL — covers sidebar clicks,
  // browser back/forward, and hard refreshes on a deep link alike. Seeded
  // correctly on first render via the useState initializer above; this only
  // needs to re-sync on subsequent navigation.
  useEffect(() => {
    if (derivedModule) {
      setActiveModule(derivedModule);
    }
  }, [derivedModule]);

  const handleModuleChange = (moduleKey) => {
    setActiveModule(moduleKey);
    // 'dashboard' is the one pageKey whose route lives at '/' rather than
    // '/dashboard' (see EXPLICIT_ROUTES below) — only Home's fallback hits
    // this today, but handled generally in case another module's fallback
    // ever becomes 'dashboard' too.
    const fallbackPageKey = moduleFallback(moduleKey);
    navigate(fallbackPageKey === 'dashboard' ? '/' : '/' + fallbackPageKey);
  };

  const handleNavigate = (pageKey) => navigate('/' + pageKey);

  const handleSearchSelect = (result) => {
    // Module matches route through handleModuleChange rather than
    // handleNavigate — it's the one place that knows 'dashboard' lives at
    // '/' instead of '/dashboard'.
    if (result.type === 'module') {
      handleModuleChange(result.moduleKey);
    } else if (result.type === 'record') {
      navigate('/commercial-entities/' + result.entityId);
    } else if (result.type === 'product') {
      navigate('/products?search=' + encodeURIComponent(result.term));
    } else if (result.type === 'price') {
      navigate('/sales-prices?product_search=' + encodeURIComponent(result.term));
    } else {
      handleNavigate(result.pageKey);
    }
    setGlobalSearch('');
    setSearchOpen(false);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      setSearchOpen(false);
      event.currentTarget.blur();
    } else if (event.key === 'Enter' && combinedSearchResults.length > 0) {
      handleSearchSelect(combinedSearchResults[0]);
    }
  };

  useEffect(() => {
    if (!searchOpen) return undefined;
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchOpen]);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar_icon_only', String(next));
      return next;
    });
  };

  // Forces the sidebar open rather than toggling it — clicking a rail icon
  // should always land you on that module's full section list, regardless
  // of whatever collapsed/expanded state it was already in.
  const expandSidebar = () => {
    setSidebarCollapsed(false);
    localStorage.setItem('sidebar_icon_only', 'false');
  };

  const moduleConfig = MODULE_TABS.find((module) => module.key === activeModule) || MODULE_TABS[0];
  const currentManualScreen = manualScreens.find((screen) => screen.page_key === helpPageKey);
  const currentScreenCode = currentManualScreen && currentManualScreen.module_number != null && currentManualScreen.sequence != null
    ? `${currentManualScreen.module_number}-${currentManualScreen.sequence}`
    : null;

  // pageKeys with a real, already-built component (mirrors the old
  // renderPage() if/else chain 1:1, minus the unreachable 'sales-currency'
  // branch — no MODULE_NAVIGATION entry ever pointed at it).
  const EXPLICIT_ROUTES = [
    { pageKey: 'dashboard', path: '/', element: <Dashboard /> },
    { pageKey: 'global-dashboard', element: <GlobalDashboard /> },
    { pageKey: 'purchasing-dashboard', element: <PurchasingDashboard /> },
    { pageKey: 'inventory-dashboard', element: <InventoryDashboard /> },
    { pageKey: 'warehouse-dashboard', element: <WarehouseDashboard /> },
    { pageKey: 'accounting-dashboard', element: <AccountingDashboard /> },
    { pageKey: 'accounting-currency', element: <CurrencyTable /> },
    { pageKey: 'products', element: <ProductTable onNavigate={handleNavigate} /> },
    { pageKey: 'inventory-product-categories', element: <ProductCategoryTable /> },
    { pageKey: 'inventory-brands', element: <BrandTable /> },
    { pageKey: 'inventory-brand-types', element: <BrandTypeTable /> },
    { pageKey: 'inventory-reports', element: <IntegrityReportPage /> },
    { pageKey: 'purchasing-reports', element: <PurchasingIntegrityReportPage /> },
    { pageKey: 'sales-reports', element: <SalesIntegrityReportPage /> },
    { pageKey: 'suppliers', element: <SupplierTable /> },
    { pageKey: 'purchasing-supplier-rebate-rules', element: <SupplierRebateRuleTable /> },
    { pageKey: 'purchasing-supplier-rebate-sku-rates', element: <SupplierRebateSkuRateTable /> },
    { pageKey: 'inventory-uom', element: <UomTable /> },
    { pageKey: 'inventory-product-types', element: <ProductTypeTable /> },
    { pageKey: 'inventory-uom-control-types', element: <UomControlTypeTable /> },
    { pageKey: 'warehouse-operation-types', element: <OperationTypeTable /> },
    { pageKey: 'sales-payment-terms', element: <PaymentTermTable /> },
    { pageKey: 'sales-shipping-services', element: <ShippingServiceTable /> },
    { pageKey: 'sales-shipping-options', element: <ShippingOptionTable /> },
    { pageKey: 'sales-australia-post', element: <AustraliaPostPage /> },
    { pageKey: 'global-document-types', element: <DocumentTypeTable /> },
    { pageKey: 'customer-customers', element: <CommercialEntityTable key="customers" initialLifecycle="customers" /> },
    { pageKey: 'sales-customers', element: <CommercialEntityTable key="sales-customers" customersOnly /> },
    { pageKey: 'customer-tiers', element: <CustomerTierTable /> },
    { pageKey: 'crm-leads', element: <CommercialEntityTable key="all" /> },
    { pageKey: 'crm-sources', element: <SourceTable /> },
    { pageKey: 'crm-close-reasons', element: <CloseReasonTable /> },
    { pageKey: 'crm-markets', element: <MarketTable /> },
    { pageKey: 'crm-channels', element: <ChannelTable /> },
    { pageKey: 'crm-operators', element: <OperatorTable /> },
    { pageKey: 'crm-legal-entity-types', element: <LegalEntityTypeTable /> },
    { pageKey: 'crm-activity-outcomes', element: <ActivityOutcomeTable /> },
    { pageKey: 'crm-enquiry-topics', element: <EnquiryTopicTable /> },
    { pageKey: 'crm-website-enquiries', element: <WebsiteEnquiriesPage /> },
    { pageKey: 'global-address-types', element: <AddressTypeTable /> },
    { pageKey: 'global-phone-types', element: <PhoneTypeTable /> },
    { pageKey: 'global-email-types', element: <EmailTypeTable /> },
    { pageKey: 'global-link-role-types', element: <LinkRoleTypeTable /> },
    { pageKey: 'global-relationship-types', element: <RelationshipTypeTable /> },
    { pageKey: 'global-entity-cards', element: <EntityCardsGrid /> },
    { pageKey: 'crm-customer-pipeline', element: <CustomerPipelinePage /> },
    { pageKey: 'crm-activities', element: <ActivitiesPage /> },
    { pageKey: 'market-intel-dashboard', element: <MarketIntelDashboard /> },
    { pageKey: 'market-intel-all-entities', element: <AllEntitiesTable /> },
    { pageKey: 'market-intel-trade-shows', element: <TradeShowTable /> },
    { pageKey: 'market-intel-trade-show-roles', element: <TradeShowAttendeeRoleTable /> },
    { pageKey: 'global-users', element: <UserTable /> },
    { pageKey: 'global-login-switcher', element: <LoginSwitcherPage /> },
    { pageKey: 'global-roles', element: <RoleTable moduleNavigation={MODULE_NAVIGATION} adminOnlyScreens={ADMIN_ONLY_SCREENS} ownPermissionScreens={OWN_PERMISSION_SCREENS} /> },
    { pageKey: 'sales-price-lookup', element: <PriceLookupPage /> },
    { pageKey: 'sales-dashboard', element: <SalesDashboard /> },
    { pageKey: 'crm-dashboard', element: <CrmDashboard /> },
    { pageKey: 'sales-quotes', element: <QuotesTable /> },
    { pageKey: 'sales-orders', element: <SalesOrdersTable /> },
    { pageKey: 'warehouse-shipments', element: <ShipmentsTable /> },
    { pageKey: 'global-document-milestones', element: <DocumentMilestonesPage /> },
    { pageKey: 'global-audit', element: <AuditLogPage /> },
    { pageKey: 'global-platform-update-check', element: <PlatformUpdateCheckPage /> },
    { pageKey: 'warehouse-pick', element: <PickOrderPage key="pick" /> },
    { pageKey: 'warehouse-pack', element: <PickOrderPage key="pack" mode="pack" /> },
    { pageKey: 'warehouse-ship', element: <PickOrderPage key="ship" mode="ship" /> },
    { pageKey: 'warehouse-fulfilment', element: <FulfilmentPage /> },
    { pageKey: 'warehouse-packaging', element: <PackagingTable /> },
    { pageKey: 'warehouse-packing-trial', element: <Suspense fallback={<div style={{ padding: 24 }}>Loading...</div>}><PackingTrialPage /></Suspense> },
    { pageKey: 'warehouse-label-sizes', element: <LabelSizeTable /> },
    { pageKey: 'warehouse-packing-station', element: <PackingStationPage /> },
    { pageKey: 'warehouse-weigh-measure', element: <WeighMeasurePage /> },
    ...Object.entries(MOVED_SCREENS).map(([from, to]) => ({ pageKey: from, element: <MovedScreenRedirect to={to} /> })),
    { pageKey: 'sales-pricelist-2', element: <PricelistTable /> },
    { pageKey: 'sales-customer-price-groups', element: <CustomerPriceGroupTable /> },
    { pageKey: 'sales-product-price-groups', element: <ProductPriceGroupTable /> },
    { pageKey: 'sales-price-rules', element: <PriceRuleTable /> },
    { pageKey: 'sales-prices', element: <PriceTable /> },
    { pageKey: 'inventory-setup', element: <InventorySettingsPage onNavigate={handleNavigate} /> },
    { pageKey: 'sales-setup', element: <SalesSettingsPage onNavigate={handleNavigate} /> },
    { pageKey: 'warehouse-setup', element: <WarehouseSettingsPage onNavigate={handleNavigate} /> },
    { pageKey: 'accounting-account-categories', element: <AccountCategoryTable /> },
    { pageKey: 'accounting-account-classes', element: <AccountClassTable /> },
    { pageKey: 'accounting-account-types', element: <AccountTypeTable /> },
    { pageKey: 'accounting-coa', element: <AccountTable /> },
    { pageKey: 'accounting-tax', element: <TaxCodeTable /> },
    { pageKey: 'accounting-tax-handling-types', element: <TaxHandlingTypeTable /> },
    { pageKey: 'accounting-setup', element: <AccountingSetupPage onNavigate={handleNavigate} /> },
    { pageKey: 'receivables-dashboard', element: <ReceivablesDashboard /> },
    { pageKey: 'receivables-setup', element: <ReceivablesSettingsPage onNavigate={handleNavigate} /> },
    { pageKey: 'receivables-payment-options', element: <PaymentOptionsPage onNavigate={handleNavigate} /> },
    { pageKey: 'company-info', element: <CompanySettings onCompanyInfoChange={loadCompanyInfo} /> },
    { pageKey: 'global-platform-settings', element: <PlatformSettingsPage onNavigate={handleNavigate} /> },
    // The signed-in staff member's own page (issue #259), from the topbar
    // user menu rather than the module nav.
    { pageKey: 'global-my-account', element: <MyAccountPage /> },
    // Local-dev-machine-only — see the matching Setup-menu filter above.
    process.env.NODE_ENV !== 'production' && { pageKey: 'global-system-admin', element: <SystemAdminPage /> },
    { pageKey: 'global-api-integrations', element: <ApiIntegrationsPage /> },
    { pageKey: 'accounting-quickbooks', element: <QuickBooksPage /> },
    // Issue #269.
    { pageKey: 'receivables-invoices', element: <InvoicesTable /> },
    { pageKey: 'receivables-receipts', element: <ReceiptsTable /> },
    { pageKey: 'global-email-templates', element: <EmailTemplateTable /> },
    { pageKey: 'global-setup-checklist', element: <SetupChecklistPage onNavigate={handleNavigate} /> },
    { pageKey: 'global-manual', element: <ManualPage /> },
    { pageKey: 'global-manual-screens', element: <ScreenSettingsTable /> },
    { pageKey: 'global-field-help', element: <FieldHelpTable /> },
    { pageKey: 'global-error-catalog', element: <ErrorCatalogPage /> },
    { pageKey: 'global-media-library', element: <MediaLibraryPage /> },
  ].filter(Boolean);
  const explicitKeys = new Set(EXPLICIT_ROUTES.map((route) => route.pageKey));

  // Every other nav item still renders the same "Coming Soon" / placeholder-
  // dashboard PlaceholderPage as before — just as its own static route now
  // instead of a shared fallthrough branch computed from mutable state.
  const placeholderRoutes = ALL_NAV_ITEMS.filter((item) => !explicitKeys.has(item.pageKey)).map((item) => {
    const itemModuleLabel = (MODULE_TABS.find((module) => module.key === item.moduleKey) || MODULE_TABS[0]).label;
    const isDashboardPage = item.pageKey.endsWith('-dashboard');
    return {
      pageKey: item.pageKey,
      element: (
        <PlaceholderPage
          title={isDashboardPage ? `${itemModuleLabel} Dashboard` : item.label}
          moduleLabel={itemModuleLabel}
          isDashboard={isDashboardPage}
        />
      ),
    };
  });

  if (!companyInfoLoaded || !access) {
    return null;
  }

  return (
    <PermissionsContext.Provider value={permissionsValue}>
    <ErrorToastProvider>
    <FieldHelpRegistryProvider>
    <HelpSheetContext.Provider value={{ openArticle: openHelpArticle }}>
    <div className={`app-shell${process.env.NODE_ENV !== 'production' ? ' app-shell--dev' : ''}`}>
      <header className="module-topbar">
        <button
          type="button"
          className="module-topbar-info module-topbar-home module-topbar-brand-col"
          onClick={() => handleModuleChange('home')}
          title="Home"
        >
          <div className="module-brand">Optim Commerce{process.env.NODE_ENV !== 'production' && '-Dev'}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
            <div className="module-company module-company-primary" style={{ marginTop: 0, minWidth: 0 }}>
              {companyInfo.company_name ? companyInfo.company_name : 'ERP'}
            </div>
          </div>
        </button>
        <div className="module-topbar-inner">
          <div className="module-search" role="search" ref={searchContainerRef}>
            <Search size={16} strokeWidth={1.75} className="module-search-icon" />
            <input
              type="search"
              value={globalSearch}
              onChange={(event) => {
                setGlobalSearch(event.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={handleSearchKeyDown}
              className="module-search-input"
              placeholder="Search modules, pages, customers, leads and SKUs"
              aria-label="Global search"
              autoComplete="off"
            />
            {searchOpen && globalSearch.trim() && (
              <div className="module-search-results">
                {combinedSearchResults.length === 0 ? (
                  <div className="module-search-empty">No modules, pages, customers, leads or SKUs match "{globalSearch.trim()}"</div>
                ) : (
                  combinedSearchResults.map((result) => (
                    <button
                      key={result.key}
                      type="button"
                      className="module-search-result"
                      onClick={() => handleSearchSelect(result)}
                    >
                      <span className="module-search-result-label">{result.label}</span>
                      <span className="module-search-result-module">
                        {result.type === 'module'
                          ? 'Module'
                          : result.type === 'record'
                            ? (result.stage ? `${result.isCustomer ? 'Customer' : 'Prospect'} · ${result.stage}` : 'Record')
                            : result.type === 'product'
                              ? 'Products'
                              : result.type === 'price'
                                ? 'Prices'
                                : getModuleLabel(result.moduleKey)}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="module-actions">
            {currentManualScreen?.enabled && (
              <button
                className="nav-btn module-nav-btn"
                title="Help for this screen"
                onClick={() => setHelpSheetOpen(true)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <HelpCircle size={20} strokeWidth={1.5} />
              </button>
            )}
            <NotificationBell />
            <UserMenu />
          </div>
        </div>
      </header>

      <HelpSheet open={helpSheetOpen} onOpenChange={closeHelpSheet} articleSlug={helpSheetArticleOverride || currentManualScreen?.article_slug} />

      <div className="app-container">
        <ModuleRail
          activeModule={activeModule}
          moduleNavigation={visibleNavigation}
          onSelectModule={handleModuleChange}
          onNavigate={handleNavigate}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebarCollapsed}
          onExpandSidebar={expandSidebar}
          navCounts={navCounts}
        />
        <Sidebar
          moduleKey={activeModule}
          moduleLabel={moduleConfig.label}
          currentPage={navPageKeyFor(currentPageKey)}
          navSections={visibleNavigation[activeModule] || []}
          onNavClick={handleNavigate}
          isOpen={true}
          collapsed={sidebarCollapsed}
          navCounts={navCounts}
        />
        <div className="main-content">
          <ScreenAccessContext.Provider value={currentAccess}>
          {!currentAccess.read ? <NoAccessPage /> : (
          <Routes>
            {EXPLICIT_ROUTES.map((route) => (
              <Route key={route.pageKey} path={route.path || `/${route.pageKey}`} element={route.element} />
            ))}
            <Route path="/products/:id/uom" element={<ProductUomPage />} />
            <Route path="/inventory-uom-control-types/:id/uoms" element={<UomControlTypeUsagePage />} />
            <Route path="/products/:id/cost-prices" element={<ProductCostPricePage />} />
            <Route path="/suppliers/:id/cost-prices" element={<SupplierCostPricesPage />} />
            <Route path="/products/:id/media" element={<ProductMediaPage />} />
            <Route path="/products/:id/weights" element={<ProductWeightsPage />} />
            <Route path="/sales-prices/:id" element={<PricePricingPage />} />
            <Route path="/pricelists/:id/generate" element={<Pricelist2Page />} />
            <Route path="/quotes/new" element={<QuotePage key="new" />} />
            <Route path="/quotes/:id" element={<QuotePage />} />
            <Route path="/sales-orders/new" element={<SalesOrderPage />} />
            <Route path="/sales-orders/:id" element={<SalesOrderPage />} />
            <Route path="/receivables-invoices/:id" element={<InvoicePage />} />
            <Route path="/receivables-receipts/:id" element={<ReceiptPage />} />
            <Route path="/accounting-invoices/:id" element={<MovedScreenRedirect to="receivables-invoices" />} />
            <Route path="/accounting-receipts/:id" element={<MovedScreenRedirect to="receivables-receipts" />} />
            <Route path="/commercial-entities/:id" element={<CommercialEntityDetailPage />} />
            {placeholderRoutes.map((route) => (
              <Route key={route.pageKey} path={`/${route.pageKey}`} element={route.element} />
            ))}
            <Route
              path="*"
              element={<PlaceholderPage title="Not Found" moduleLabel={moduleConfig.label} isDashboard={false} />}
            />
          </Routes>
          )}
          </ScreenAccessContext.Provider>
        </div>
      </div>

      {currentScreenCode && (
        <div className="screen-code-badge" title="Screen reference code">Page # {currentScreenCode}</div>
      )}
    </div>
    </HelpSheetContext.Provider>
    </FieldHelpRegistryProvider>
    </ErrorToastProvider>
    </PermissionsContext.Provider>
  );
}

export default App;